import {
  anchorHit,
  careerStats,
  CONFIG,
  deriveResult,
  getMarket,
  gradeLeg,
  hitRatePercent,
  legMultiplier,
  scorerIdsFromEvents,
  selectionLabel,
  type Fixture,
  type MarketDef,
  type Membership,
  type Outcome,
  type PredictionLeg
} from '@h2h/shared';
import { formatMultiplier } from '../../lib/format';
import type { PlayerDuelPickReveal, PlayerPickReveal } from '../../lib/api';
import type { DuelStatus, DuelVM } from '../../lib/cyclingRound';

export interface StatLine {
  seasonPoints: number;
  rounds: number;
  promotions: number;
  bestRank: number | null;
  exactHits: number;
  hitRate: number | null;
}

/** Aggregate a player's career stat line across every league membership. */
export function statLine(memberships: Membership[]): StatLine {
  const career = careerStats(memberships);
  const rounds = career?.roundsPlayed ?? 0;
  const bestRank = memberships.reduce<number | null>((best, m) => {
    const r = m.bestRankInGroup;
    if (r == null) return best;
    return best == null ? r : Math.min(best, r);
  }, null);
  const hitRate = hitRatePercent(career);
  return {
    seasonPoints: career?.seasonPoints ?? 0,
    rounds,
    promotions: career?.promotions ?? 0,
    bestRank,
    exactHits: career?.exactScoreHits ?? 0,
    hitRate
  };
}

export function joinedLabel(createdAt: number | undefined): string {
  if (!createdAt) return '';
  return new Date(createdAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
}

export type MarketState = 'hit' | 'miss' | 'ontrack' | 'offtrack' | 'open';

export const STATE_LABEL: Record<MarketState, string> = {
  hit: 'hit',
  miss: 'miss',
  ontrack: 'hitting',
  offtrack: 'pending',
  open: 'open'
};

/** The 1X2 base, in words. */
export function profileOutcomeWord(o: Outcome, home: string, away: string): string {
  if (o === 'HOME') return `${home} win`;
  if (o === 'AWAY') return `${away} win`;
  return 'Draw';
}

/** The display value for one boost (option label, scorer name, or exact score). */
export function profileLegValue(leg: PredictionLeg, m: MarketDef, teams: { home: string; away: string }): string {
  if (m.kind === 'player') return leg.label ?? 'Scorer';
  if (m.kind === 'exact') return leg.selection.replace('-', '–');
  return selectionLabel(leg.marketId, leg.selection, teams);
}

/**
 * The full breakdown of a revealed card — the 1X2 base first, then one row per stacked
 * boost — each with its selection and hit/miss/on-track state. Graded through the shared
 * registry (Build-Your-Card §7) exactly like the Match-detail rows, so a profile reads
 * like the rest of the app. Picks are only revealed once their fixture has kicked off,
 * so a state is always meaningful (live or final), never a pre-kickoff "open".
 */
export function fixtureMarketRows(
  pick: PlayerPickReveal,
  fixture: Fixture | undefined
): { label: string; value: string; state: MarketState; factor: string }[] {
  if (!fixture) return [];
  const result = fixture.result ?? null;
  const final = fixture.status === 'SETTLED' && !!result;
  const live = fixture.status === 'LIVE';
  const liveHt =
    fixture.liveHalftimeHome != null && fixture.liveHalftimeAway != null
      ? { home: fixture.liveHalftimeHome, away: fixture.liveHalftimeAway }
      : null;
  const liveResult =
    live && (fixture.liveHomeGoals != null || fixture.liveAwayGoals != null)
      ? deriveResult(
          fixture.fixtureId,
          fixture.liveHomeGoals ?? 0,
          fixture.liveAwayGoals ?? 0,
          scorerIdsFromEvents(fixture.events),
          liveHt
        )
      : null;
  const teams = { home: fixture.homeTeam, away: fixture.awayTeam };
  const gRes = final && result ? result : live && liveResult ? liveResult : null;
  const rows: { label: string; value: string; state: MarketState; factor: string }[] = [];

  const baseHit = gRes ? anchorHit(pick, gRes) : false;
  rows.push({
    label: 'Match result',
    value: profileOutcomeWord(pick.outcome, fixture.homeTeam, fixture.awayTeam),
    state: gRes ? (final ? (baseHit ? 'hit' : 'miss') : baseHit ? 'ontrack' : 'offtrack') : 'open',
    factor: `+${CONFIG.COMBO.BASE_POINTS}`
  });

  for (const leg of pick.legs ?? []) {
    const m = getMarket(leg.marketId);
    if (!m) continue;
    const g = gRes ? gradeLeg(leg, gRes) : { gradeable: false, hit: false };
    const state: MarketState = !g.gradeable
      ? 'open'
      : final
        ? g.hit
          ? 'hit'
          : 'miss'
        : g.hit
          ? 'ontrack'
          : 'offtrack';
    const missed = state === 'miss' || state === 'offtrack';
    rows.push({
      label: m.label,
      value: profileLegValue(leg, m, teams),
      state,
      factor: `${missed ? '÷' : '×'}${formatMultiplier(legMultiplier(leg.marketId, leg.selection))}`
    });
  }
  return rows;
}

/** A short "Sat 14 Dec" label for a round's first kickoff. */
export function roundDateLabel(ms: number | undefined): string {
  if (!ms) return '';
  return new Date(ms).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Rebuild the app's standard duel view-model from a revealed profile pick, so a cycling
 *  call on a profile renders with the very same card as the live board and settled recap. */
export function duelVMFromReveal(pick: PlayerDuelPickReveal, fixture: Fixture | undefined): DuelVM {
  const aName = fixture?.riderA?.riderName ?? fixture?.homeTeam ?? '—';
  const bName = fixture?.riderB?.riderName ?? fixture?.awayTeam ?? '—';
  const dr = fixture?.duelResult ?? null;
  const status: DuelStatus =
    fixture?.status === 'VOID' || dr?.outcome === 'void'
      ? 'void'
      : fixture?.status === 'SETTLED' && dr
        ? 'settled'
        : 'upcoming';
  const winner = dr?.winner ?? null;
  return {
    duelId: pick.duelId,
    speciality: fixture?.speciality ?? null,
    riderAName: aName,
    riderBName: bName,
    riderATeam: fixture?.riderA?.teamName ?? null,
    riderBTeam: fixture?.riderB?.teamName ?? null,
    riderANat: fixture?.riderA?.nationality ?? null,
    riderBNat: fixture?.riderB?.nationality ?? null,
    pick: pick.pick,
    pickedName: pick.pick === 'A' ? aName : bName,
    otherName: pick.pick === 'A' ? bName : aName,
    confidence: pick.confidenceWeight,
    status,
    winner,
    correct: status === 'settled' ? winner === pick.pick : null,
    points: pick.points,
    kickoffAt: fixture?.kickoffAt ?? 0
  };
}
